import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { MonitorX, Unlock, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PageHeader } from "@/components/PageHeader";
import { api, errMsg, fmtDate } from "@/lib/api";

const DeviceCard = ({ d, onUnblock }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid={`device-${d.id}`}>
    <div className="flex flex-wrap items-start gap-3">
      <div className={`w-10 h-10 rounded-md grid place-items-center ${d.flagged ? "bg-[#FFF0F0] text-[#E60000]" : "bg-gray-100 text-gray-500"}`}><MonitorX className="w-5 h-5" /></div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold">{d.label || "Unknown browser"}</div>
        <div className="text-xs text-gray-500">{d.flagged ? `Flagged ${fmtDate(d.flagged_at)}` : `Unblocked ${d.unblocked_at ? fmtDate(d.unblocked_at) : ""}${d.unblocked_by ? ` by ${d.unblocked_by}` : ""}`} · {d.accounts.length} account(s) · limit {d.limit}{d.blocked_attempts ? ` · ${d.blocked_attempts} blocked attempt(s)` : ""}</div>
      </div>
      {d.flagged && <button onClick={() => onUnblock(d)} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#E5E7EB] text-sm font-semibold hover:border-[#E60000] transition-colors" data-testid={`device-unblock-${d.id}`}><Unlock className="w-4 h-4" />Unblock</button>}
    </div>
    <ul className="mt-3 divide-y border-t text-sm" data-testid={`device-accounts-${d.id}`}>
      {d.accounts.map((a) => <li key={a.member_id} className="py-2 flex flex-wrap justify-between gap-2"><span className="font-medium">{a.name}<span className="text-gray-500 font-normal"> · {a.email}</span></span><span className="text-xs text-gray-500">{fmtDate(a.created_at)}</span></li>)}
    </ul>
  </div>
);

export default function FlaggedDevices() {
  const [list, setList] = useState(null);
  const [all, setAll] = useState(false);
  const [q, setQ] = useState("");
  const [ask, setAsk] = useState(null);
  const load = useCallback(() => api.get("/devices", { params: { flagged: !all } }).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e))), [all]);
  useEffect(() => { load(); }, [load]);
  const unblock = async () => {
    try { await api.post(`/devices/${ask.id}/unblock`); toast.success("Device unblocked. It can create 2 more accounts"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setAsk(null);
  };
  const rows = (list || []).filter((d) => `${d.label} ${d.accounts.map((a) => `${a.name} ${a.email}`).join(" ")}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="flagged-devices-page">
      <PageHeader eyebrow="Control" title="Flagged Devices" description="Browsers that have already been used to create 2 member accounts. They can still sign in, but cannot create another account unless you unblock them." />
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center mb-4">
        <div className="relative flex-1 max-w-md"><Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by member name or email" className="pl-9 h-10 bg-white" data-testid="devices-search" /></div>
        <label className="flex items-center gap-2 text-sm cursor-pointer"><Switch checked={all} onCheckedChange={setAll} data-testid="devices-show-all" />Show all tracked devices</label>
      </div>
      {!list ? <div className="text-gray-500">Loading…</div> : <div className="space-y-3">
        {rows.map((d) => <DeviceCard key={d.id} d={d} onUnblock={setAsk} />)}
        {!rows.length && <div className="p-10 text-center text-sm text-gray-500 bg-white border rounded-lg" data-testid="devices-empty">{all ? "No devices recorded yet." : "No flagged devices."}</div>}
      </div>}
      <AlertDialog open={!!ask} onOpenChange={() => setAsk(null)}>
        <AlertDialogContent data-testid="device-unblock-dialog">
          <AlertDialogHeader><AlertDialogTitle>Unblock this device?</AlertDialogTitle>
            <AlertDialogDescription>It will be able to create 2 more member accounts. It will be flagged again once it reaches the new limit.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="device-unblock-cancel">Cancel</AlertDialogCancel><AlertDialogAction onClick={unblock} data-testid="device-unblock-confirm">Unblock</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
